import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Select, FormInstance } from 'antd';
import MultiCascader from 'antd-multi-cascader';
import { NamePath } from 'antd/lib/form/interface';
import classNames from 'classnames';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

import 'antd-multi-cascader/dist/index.css';

import { FilterTag } from './filter-tag';
import { debounce as _debounce } from 'lodash';
import { Translations } from '../filter-bar-2';
import { FilterBar2Value } from '../types';
import Checkbox from 'components/checkbox/checkbox';
import SelectAllSwitch, { DEFAULT_SELECT_ALL_THRESHOLD } from '../../common/select-all-switch/select-all-switch';

export interface SelectValue {
	label: string;
	value: any;
	children?: SelectValue;
}

export interface FilterContentSelectProps {
	name: NamePath;
	values?: FilterBar2Value[];
	multiselect?: boolean;
	placeholder?: string;
	className?: string;
	allowClear?: boolean;
	translations: Translations & { selectAllTagText?: string };
	onChange?: (value: any, option: any) => void;
	onSearch?: (value: string, key: NamePath) => Promise<any>;
	form: FormInstance;
	allowSelectAll?: boolean;
	emptyAsAll?: boolean;
	selectAllThreshold?: number;
	maxTagCount?: number | 'responsive';
	maxTagPlaceholder?: string;
	selectAllTagText?: string;
}

export const FilterContentSelect = ({
	name,
	values,
	placeholder,
	multiselect,
	className,
	onChange,
	onSearch,
	form,
	allowClear = true,
	allowSelectAll = false,
	emptyAsAll = false,
	translations,
	selectAllThreshold = DEFAULT_SELECT_ALL_THRESHOLD,
	maxTagCount,
	maxTagPlaceholder,
	selectAllTagText
}: FilterContentSelectProps) => {
	// TODO: the only way to make checkboxes be rendered with a actual value
	// easy can be refactored to one line with hook .useWatch from new atnd version
	const [currentValues, setCurrentValues] = useState([]);
	const [listValues, setListValues] = useState(values);
	const [searchValue, setSearchValue] = useState('');
	const selectedValues = Form.useWatch(name, form);
	const isCascade = useMemo(() => listValues?.some(item => item.children), [listValues]);

	const allValues = useMemo(() => {
		return listValues?.map(item => item.value) || [];
	}, [listValues]);

	useEffect(() => {
		if (emptyAsAll && selectedValues?.length === 0) {
			return;
		}

		setCurrentValues(selectedValues);
	}, [selectedValues, emptyAsAll]);

	useEffect(() => {
		setListValues(values);
	}, [values]);

	const onValueChange = useCallback(
		(value, option) => {
			form.setFields([{ name: name, value: emptyAsAll && value.length === allValues.length ? [] : value }]);
			setCurrentValues(value);
			onChange?.(value, option);
		},
		[allValues.length, emptyAsAll, form, name, onChange]
	);

	const filterOption = (input, { label }) => label.toLowerCase().indexOf(input.toLowerCase().trim()) >= 0;

	const filteredValues = useMemo(() => {
		if (!searchValue) {
			return listValues;
		}

		return listValues?.filter(item => filterOption(searchValue, item));
	}, [searchValue, listValues]);

	const { isAllSelected, someSelected } = useMemo(() => {
		const result = {
			isAllSelected: false,
			someSelected: false
		};

		if (!currentValues || !filteredValues || !Array.isArray(currentValues)) {
			return result;
		}

		const currentValuesSet = new Set(currentValues);

		// every stops when something is false
		result.isAllSelected = filteredValues.every(item => currentValuesSet.has(item.value));
		// some stops when something is true
		result.someSelected = filteredValues.some(item => currentValuesSet.has(item.value));
		// no need to go over entire array even once

		return result;
	}, [currentValues, filteredValues]);

	const handleSelectAll = useCallback(
		(checked: boolean) => {
			if (checked) {
				onValueChange(
					[...(currentValues ? currentValues : []), ...filteredValues.map(item => item.value)],
					listValues
				);
			} else if (filteredValues.length === listValues.length) {
				// unselect all values
				onValueChange([], listValues);
			} else {
				// unselect only filtered values
				const filteredValuesSet = new Set(filteredValues.map(item => item.value));
				onValueChange(
					allValues.filter(value => !filteredValuesSet.has(value)),
					listValues
				);
			}
		},
		[listValues, onValueChange, filteredValues]
	);

	const onSearchValue = _debounce(async (value: string) => {
		const trimmedValue = value.trim();

		if (trimmedValue.length < 2) {
			return;
		}

		const lookupListValues = await onSearch(trimmedValue, name);

		const previousSelectedValues = currentValues?.map(v => listValues.find(item => item.value === v));
		const newList = previousSelectedValues?.length
			? lookupListValues.concat(previousSelectedValues)
			: lookupListValues;
		const uniqueList = [...new Map(newList.map(item => [item.value, item])).values()] as FilterBar2Value[];
		setListValues(uniqueList);
	}, 500);

	const cascadeValues = useMemo(() => {
		const process = data =>
			data.map(item => ({
				value: item.value,
				title: item.label,
				children: Array.isArray(item.children) ? process(item.children) : []
			}));

		return process(listValues);
	}, [listValues]);

	const stopPropagationMouseDownEvent = useCallback((event: React.MouseEvent<HTMLSpanElement>) => {
		event.stopPropagation();
	}, []);

	const onSearchCallback = useMemo(() => {
		if (!onSearch) {
			return (value: string) => setSearchValue(value);
		}

		return (value: string) => {
			setSearchValue(value);
			return onSearchValue(value);
		};
	}, [onSearch, onSearchValue]);

	if (isCascade) {
		// TODO: we can switch back to this component when updated to ^4.17.0 https://github.com/bringg/react-components/pull/750
		return (
			<Form.Item name={name}>
				<MultiCascader placeholder={placeholder} data={cascadeValues} onChange={onChange} />
			</Form.Item>
		);
	}

	return (
		<>
			<Form.Item>
				<Select
					value={currentValues}
					// TODO: with new antd version we will be able to use our own icon for clear
					allowClear={allowClear}
					showArrow
					className={className}
					mode={multiselect ? 'multiple' : undefined}
					showSearch
					filterOption={filterOption}
					placeholder={placeholder}
					suffixIcon={<BringgIcon className="filter-chevron-icon" iconName={BringgFontIcons.Chevron} />}
					tagRender={props => (
						<FilterTag
							closable
							reversed={false}
							onClose={props.onClose}
							onMouseDown={stopPropagationMouseDownEvent}
						>
							{listValues?.find(val => val.value === props.value)?.label}
						</FilterTag>
					)}
					searchValue={searchValue}
					onSearch={onSearchCallback}
					onChange={onValueChange}
					popupClassName={classNames('filter-bar-select', { multiselect })}
					data-test-id={`${name}-select-input`}
					maxTagCount={isAllSelected && selectAllTagText ? 0 : maxTagCount}
					maxTagPlaceholder={isAllSelected && selectAllTagText ? selectAllTagText : maxTagPlaceholder}
					onDropdownVisibleChange={() => setSearchValue('')}
					dropdownRender={menu => (
						<div data-test-id={`${name}-select-dropdown`}>
							{multiselect && allowSelectAll && filteredValues?.length > selectAllThreshold && (
								<>
									<SelectAllSwitch
										className="select-all-switch"
										selectAllText={translations.selectAll}
										onSelectAll={handleSelectAll}
										checked={isAllSelected}
										indeterminate={!isAllSelected && someSelected}
										allValuesCount={filteredValues.length}
									/>
									<div className="select-all-divider" />
								</>
							)}
							{menu}
						</div>
					)}
				>
					{listValues.map(item => (
						<Select.Option title={item.label} label={item.label} value={item.value} key={item.value}>
							{multiselect && <Checkbox checked={currentValues?.includes(item.value)} />}
							{item.label}
						</Select.Option>
					))}
				</Select>
			</Form.Item>

			{/*	for useWatch to work we need Form.Item name={name} */}
			{/*	but we don't want Form.Item to update Select values */}
			<Form.Item name={name} hidden />
		</>
	);
};
